import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Grid,
  Box,
  TextField,
  Button,
  Autocomplete,
  Typography,
} from "@mui/material";
import Toast from "./Toast";
import { useLocation } from "react-router-dom";
import bannerVideo from '../assets/img/Home/home screen video.mp4';
import { createSalesAndLettings } from "../services/propertyService";

const validationSchema = yup.object({
  firstname: yup.string().required("First Name is required"),
  lastname: yup.string().required("Last Name is required"),
  number: yup.string().matches(/^(?:\+44|0)7\d{9}$/, "Invalid UK mobile number").required("Contact Number is required"),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  location: yup.string().required("Location is required"),
  bedroom: yup.string().required("Bedroom is required"),
  type: yup.string().required("Property Type is required"),
  category: yup.string().required("Category is required"),
});

export const EnquireUs = () => {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const location = useLocation();
  const initialCategory = new URLSearchParams(location.search).get("category") || "Sales";

  const [typeOptions] = useState([
    { id: 1, label: "House" },
    { id: 2, label: "Bungalow" },
    { id: 3, label: "Flat" },
    { id: 4, label: "Maisonette" },
    { id: 5, label: "Cottage" },
    { id: 6, label: "Farm" },
    { id: 7, label: "Development Plot" },
    { id: 8, label: "Apartment" },
  ]);

  const [bedroomOptions] = useState([
    { id: 1, label: "1 Bed" },
    { id: 2, label: "2 Beds" },
    { id: 3, label: "3 Beds" },
    { id: 4, label: "4 Beds" },
    { id: 5, label: "5 Beds" },
    { id: 6, label: "6 Beds" },
    { id: 7, label: "7 Beds" },
    { id: 8, label: "8 Beds" },
  ]);

  const lettingOptions = useMemo(() => [
    { id: 1, label: "Full Property Management" },
    { id: 2, label: "Rent Collection Service" },
    { id: 3, label: "Tenant Find Service" },
  ], []);

  const initialValues = {
    firstname: "",
    lastname: "",
    email: "",
    number: "",
    location: "",
    bedroom: "",
    type: "",
    category: initialCategory === "Sales" ? "Sales" : "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: (values,{ resetForm }) => {
      const formValues = {
        ...values,
        form_type: initialCategory,
      };
      createSalesAndLettings(formValues)
        .then((res) => {
          console.log('Response:', res);
          setToastMessage(res.message);
          setToastOpen(true);
          resetForm();
        })
        .catch((error) => {
          console.error('Error:', error);
          // setToastMessage("There was an error submitting the form. Please try again.");
          // setToastOpen(true);
        });
    },
  });

  useEffect(() => {
    if (initialCategory === "Lettings") {
      setCategoryOptions(lettingOptions);
    } else if (initialCategory === "Sales") {
      setCategoryOptions([{ id: 1, label: "Sales" }]);
    }
  }, [initialCategory, lettingOptions]);

  return (
    <>
      <div
        className="banner-area"
        style={{
          position: "relative",
          height: "50vh",
          overflow: "hidden",
        }}
      >
        <video
          autoPlay
          muted
          loop
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: -1,
          }}
        >
          <source src={bannerVideo} type="video/mp4" />
        </video>

        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            zIndex: -1,
          }}
        ></div>

        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "white",
            textAlign: "center",
            zIndex: 1,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: "3rem",
              fontWeight: "800",
              fontFamily: "Ibarra Real Nova",
              color: "white !important",
              textShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
              marginBottom: "20px",
              "@media (max-width: 600px)": {
                fontSize: "2rem",
              },
              "@media (max-width: 400px)": {
                fontSize: "1.6rem",
              },
            }}
          >
            Enquire Now
          </Typography>
        </div>
      </div>

      <Box className="container py-4">
        <h3>Enquire Now</h3>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <label htmlFor="firstname" className="form-label">First Name</label>
              <TextField
                fullWidth
                name="firstname"
                placeholder="First Name"
                value={formik.values.firstname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstname && Boolean(formik.errors.firstname)}
                helperText={formik.touched.firstname && formik.errors.firstname}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <label htmlFor="lastname" className="form-label">Last Name</label>
              <TextField
                fullWidth
                name="lastname"
                placeholder="Last Name"
                value={formik.values.lastname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastname && Boolean(formik.errors.lastname)}
                helperText={formik.touched.lastname && formik.errors.lastname}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <label htmlFor="number" className="form-label">Contact Number</label>
              <TextField
                fullWidth
                name="number"
                placeholder="Contact Number"
                value={formik.values.number}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.number && Boolean(formik.errors.number)}
                helperText={formik.touched.number && formik.errors.number}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <label htmlFor="email" className="form-label">Email</label>
              <TextField
                fullWidth
                name="email"
                placeholder="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <label htmlFor="location" className="form-label">Location</label>
              <TextField
                fullWidth
                name="location"
                placeholder="Location"
                value={formik.values.location}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.location && Boolean(formik.errors.location)}
                helperText={formik.touched.location && formik.errors.location}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <label htmlFor="bedroom" className="form-label">Bedroom</label>
              <Autocomplete
                freeSolo
                options={bedroomOptions}
                value={formik.values.bedroom}
                onInputChange={(event, newInputValue) => {
                  formik.setFieldValue("bedroom", newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="bedroom"
                    placeholder="Select Bedroom"
                    variant="outlined"
                    error={formik.touched.bedroom && Boolean(formik.errors.bedroom)}
                    helperText={formik.touched.bedroom && formik.errors.bedroom}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <label htmlFor="type" className="form-label">Property Type</label>
              <Autocomplete
                freeSolo
                options={typeOptions}
                value={formik.values.type}
                onInputChange={(event, newInputValue) => {
                  formik.setFieldValue("type", newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="type"
                    placeholder="Select Property Type"
                    variant="outlined"
                    error={formik.touched.type && Boolean(formik.errors.type)}
                    helperText={formik.touched.type && formik.errors.type}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <label htmlFor="category" className="form-label">Category</label>
              <Autocomplete
                freeSolo
                options={categoryOptions}
                value={formik.values.category}
                onInputChange={(event, newInputValue) => {
                  formik.setFieldValue("category", newInputValue);
                }}
                disabled={formik.values.category === "Sales"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="category"
                    placeholder={formik.values.category === "Sales" ? "Sales" : "Select Category"}
                    variant="outlined"
                    error={formik.touched.category && Boolean(formik.errors.category)}
                    helperText={formik.touched.category && formik.errors.category}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: "#d8b035",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#c5a02f",
                  },
                }}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </form>
        <Toast
          open={toastOpen}
          message={toastMessage}
          onClose={() => setToastOpen(false)}
          severity="success"
          autoHideDuration={6000}
        />
      </Box>
    </>
  );
};
